import type { FC } from 'react'
import React, { useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useUserRegion } from '@frontend/components/agromarket/components/RegionSelector'
import { CommonLink } from '@frontend/pole-ui/lib/components/CommonLink'
import { Icon } from '@frontend/pole-ui/lib/components/Icon'
import { ShevronRightIcon } from '@frontend/pole-ui/lib/components/Icon/svg/ShevronRightIcon'
import { LotBanner } from '@/components/composed/harvestSaleLots'
import Lot from '@/components/composed/harvestSaleLots/Lot/Lot'
import { openModal } from '@/components/composed/Modal/actions'
import { Skeleton } from '@/components/ui'
import type { IUserGtmInfoPayload } from '@/interfaces'
import { pushBtnBannerHarvestGtmEvent, pushHarvestNameClickGtmEvent } from '@/logic/metrika/harvest/gtmEvents'
import { pushGtmEventOnBlockHarvestSaleCultureLotLinkClick } from '@/logic/metrika/harvest/helpers'
import { getUserGtmInfo } from '@/logic/metrika/helpers'
import type { IRequestHarvestSaleByTemplateModalOptions } from '@/pages/HarvestSaleLots/components/RequestHarvestSaleByTemplateModal/interfaces'
import type { RootState } from '@/redux/interfaces'
import type { ITemplate } from '@/types/HarvestSaleLots'
import { getCulturePageUrl, getHarvestSaleLotsUrl } from '@/utils/outputs/url'
import { harvestSaleLotsPreviewSkeletons } from './constants'
import type { IHarvestSaleLotsPreviewProps } from './interfaces'
import { messages } from './messages'
import './styles.scss'

const mapState = (state: RootState) => ({
  userGtmInfo: getUserGtmInfo()(state),
})

const HarvestSaleLotsPreview: FC<IHarvestSaleLotsPreviewProps> = props => {
  const { culture, isAgrarian, isMobileDevice, isLoading, saleTemplatesByCulture, count, withBanner, isAuthenticated } = props
  const { userGtmInfo } = useSelector(mapState)
  const { userRegion } = useUserRegion()
  const { id: cultureId, name: cultureName } = culture || {}
  const otherLotsQuantity = (count || 0) - (withBanner ? 2 : 3) || 0
  const dispatch = useDispatch()

  const openRequestingHarvestSaleModal = useCallback(
    (template: ITemplate) =>
      dispatch(
        openModal({
          options: { dialogId: 'RequestHarvestSaleByTemplateModal' },
          contentOptions: { template, sourceOfOpening: 'previewLotsOutputs' } as IRequestHarvestSaleByTemplateModalOptions,
        }),
      ),
    //eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const pushGtmEvent = useCallback(() => {
    pushBtnBannerHarvestGtmEvent({
      items: [{ name: 'applicationBanner' }],
      pagePart: 'content',
      blockName: 'blockPreviewOutputs',
      eventCategory: 'ecommerce',
      eventAction: 'click',
      eventLabel: 'buttonHarvestSaleMulti',
      eventStatus: 'success',
      ecommerceType: 'add',
      eventEcommerce: 'addToCart',
      eventNonInteraction: '0',
      actionField: {
        list: 'previewLotsOutputs',
      },
      trueEventContext: 'lst:previewLotsOutputs',
      regionSelected: userRegion.name,
      userGtmInfo,
    })
  }, [userGtmInfo, userRegion.name])

  const pushCultureNameClickGtmEvent = (eventLabel: string, regionSelected: string, userInfo: IUserGtmInfoPayload) => {
    pushHarvestNameClickGtmEvent(
      {
        items: [],
        blockName: 'blockHarvestSaleCultureLink',
        eventCategory: 'interactions',
        eventAction: 'click',
        eventLabel,
        eventStatus: 'success',
        eventNonInteraction: '0',
        trueEventContext: null,
      },
      regionSelected,
      userInfo,
    )
  }

  if (!saleTemplatesByCulture?.length && !isLoading) return null

  const lotsUrl = isAgrarian || !isAuthenticated ? getCulturePageUrl(cultureId) : getHarvestSaleLotsUrl(cultureId)
  const alLlotsUrl = getHarvestSaleLotsUrl(cultureId)

  return (
    <section className="harvest-sale-lots-preview-container page-wrapper">
      <h2 className="h3 harvest-sale-lots-preview-container-title space-holder16">
        <CommonLink
          url={lotsUrl}
          className="row-align_center harvest-sale-lots-preview-container-title__link"
          onClick={() => pushCultureNameClickGtmEvent(cultureId, userRegion.name, userGtmInfo)}
        >
          <span className="space-holder-right8-from-sm space-holder-right-auto-sm">{cultureName}</span>
          <Icon component={<ShevronRightIcon />} />
        </CommonLink>
      </h2>

      <Skeleton isLoading={isLoading && !saleTemplatesByCulture} skeletons={harvestSaleLotsPreviewSkeletons}>
        {(withBanner ? saleTemplatesByCulture?.slice(0, 2) : saleTemplatesByCulture)?.map(template => (
          <section key={template.id} className="harvest-sale-lot-preview row row_column">
            <Lot
              template={template}
              openRequestingHarvestSaleModal={openRequestingHarvestSaleModal}
              isAgrarian={isAgrarian}
              isMobileDevice={isMobileDevice}
            />
          </section>
        ))}

        {withBanner && <LotBanner className="harvest-sale-lot-banner" isMobileView onButtonClick={pushGtmEvent} />}

        {otherLotsQuantity > 0 && (
          <div style={{ width: '100%' }}>
            <CommonLink
              url={alLlotsUrl}
              className="space-holder-top16 button button_white harvest-sale-lots-preview-container__more-lots-link"
              onClick={() => pushGtmEventOnBlockHarvestSaleCultureLotLinkClick(userGtmInfo, userRegion.name, cultureId)}
            >
              {messages.moreLots}
            </CommonLink>
          </div>
        )}
      </Skeleton>
    </section>
  )
}

export default HarvestSaleLotsPreview
